import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authAPI } from '../api/client'

/**
 * Login page component.
 *
 * Provides authentication form for users to log in to the admin dashboard.
 * Displays:
 * - Username input field
 * - Password input field
 * - Submit button
 * - Error messages (if authentication fails)
 *
 * On successful login:
 * - The server sets an HttpOnly session cookie
 * - User data is stored in AuthContext
 * - User is redirected to dashboard
 */

export interface LoginProps {
  /** Page heading; the admin bundle passes "hPanel Admin". */
  heading?: string
  /** Offer "Create an account" when public registration is open (tenant only). */
  showRegisterLink?: boolean
}

export const Login: React.FC<LoginProps> = ({ heading = 'hPanel', showRegisterLink = false }) => {
  const { login } = useAuth()
  const [signupOpen, setSignupOpen] = useState(false)

  useEffect(() => {
    if (!showRegisterLink) return
    authAPI
      .registrationStatus()
      .then((r) => setSignupOpen(r?.enabled === true))
      .catch((e) => {
        // Optional affordance: on failure just don't offer the link.
        // eslint-disable-next-line no-console
        console.error('[Login] registration status probe failed', e)
      })
  }, [showRegisterLink])
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [needsTotp, setNeedsTotp] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // Hidden until the server confirms recovery email is configured; a link
  // that leads to a 503 is a dead end (launch QA 2026-10-03, H2).
  const [resetAvailable, setResetAvailable] = useState(false)

  useEffect(() => {
    let cancelled = false
    authAPI
      .passwordResetAvailability()
      .then(r => { if (!cancelled) setResetAvailable(r?.available === true) })
      .catch(e => {
        console.error('[login] password-reset availability check failed', e)
        if (!cancelled) setResetAvailable(false)
      })
    return () => { cancelled = true }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      // Call login from AuthContext. Returns the authenticated profile so the
      // landing decision can be made here without waiting for the context
      // state to propagate into this render.
      const authed = await login(username, password, totpCode || undefined)

      // Send the user to the plane they belong to, by ABSOLUTE URL and via a
      // full page load. This component is shared by both bundles and is
      // reachable at `/login` (tenant) and `/admin/login` (admin), so the
      // destination is frequently in the *other* bundle — which react-router
      // cannot reach. The previous `navigate('/dashboard')` was relative to
      // whichever bundle happened to render the form: in the tenant bundle it
      // matched no route and fell through the catch-all to /client, so an
      // admin signing in at `/login` silently landed in the tenant console.
      const requested = location.state?.returnTo
      const allowed = typeof requested === 'string'
        && !requested.includes('\\')
        && (/^\/client(?:[/?#]|$)/.test(requested)
          || (authed.role === 'admin' && /^\/admin\/(?!login(?:[/?#]|$)|register(?:[/?#]|$))/.test(requested)))
      window.location.assign(allowed ? requested : authed.role === 'admin' ? '/admin/dashboard' : '/client')
    } catch (err) {
      // Display error message
      if (err instanceof Error) {
        if (err.message === 'TOTP code required') setNeedsTotp(true)
        setError(err.message)
      } else {
        setError('Login failed. Please check your credentials.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div data-testid="login-page" className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-center text-3xl font-bold text-gray-900 dark:text-gray-100">
            {heading}
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-700 dark:text-gray-300">
            Sign in to your account
          </h2>
        </div>

        {/* Login Form */}
        <form data-testid="login-form" className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                data-testid="login-username-input"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your username"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your password"
              />
            </div>
          </div>

          {needsTotp && <div>
            <label htmlFor="totp-code">Authenticator code</label>
            <input id="totp-code" data-testid="login-totp-input" autoComplete="one-time-code"
              inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required
              value={totpCode} onChange={e => setTotpCode(e.target.value)}
              className="block w-full rounded border p-3 text-gray-900" />
          </div>}
          {resetAvailable && (
            <a href="/password-reset" className="block text-blue-600">Forgot your password?</a>
          )}
          {signupOpen && (
            <a href="/register" className="block text-blue-600">Create an account</a>
          )}

          {/* Error Message */}
          {error && (
            <div data-testid="login-error" role="alert" className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
